import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from '../hooks/useWindowSize';
import { API } from '../api';
import { C, E, RAYON, T, bouton, pastille } from '../theme';
import { REGLES } from '../marque';
import Carte, { TitreGroupe } from '../components/Carte';
import Indicateur from '../components/Indicateur';
import IndicateurDouble from '../components/IndicateurDouble';
import { SqueletteIndicateurs } from '../components/Squelette';
import AgendaTableauBord from '../components/AgendaTableauBord';
import { TYPE_CONTRAT_LABELS } from './Assurance';

function Dashboard({ user }) {
  const { isMobile, isTablet } = useWindowSize();
  const navigate = useNavigate();
  // Même nombre de colonnes pour tous les groupes : sans cela, une rangée de
  // six cartes se répartit en 5 + 1 et rompt l'alignement avec les voisines.
  const grilleIndicateurs = {
    display: 'grid', gap: E.md,
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)'
      : isTablet ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
  };

  const [prospects, setProspects] = useState([]);
  const [projets, setProjets] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [rappels, setRappels] = useState([]);
  const [rappelsLoading, setRappelsLoading] = useState(false);
  const [alertesBanques, setAlertesBanques] = useState([]);
  const [contratsActifsModal, setContratsActifsModal] = useState(false);
  const [choixChaudes, setChoixChaudes] = useState(false);
  // Liste des affaires chaudes d'un métier ('credit' | 'assurance' | null), où l'on
  // peut supprimer une affaire — et elle seule, jamais l'entreprise.
  const [listeChaudes, setListeChaudes] = useState(null);
  const [erreurAffaire, setErreurAffaire] = useState('');
  // Vrai tant que les données ne sont pas revenues. Sans cela le tableau de
  // bord affiche tous ses indicateurs à zéro puis se corrige : on lit « 0 € »
  // de chiffre d'affaires avant de voir le vrai montant.
  const [chargement, setChargement] = useState(true);

  // Les tâches et rendez-vous du bloc « À faire ». Rechargés après chaque
  // ajout ou suppression plutôt que mis à jour à la main : la liste est
  // courte, et deux états qui divergent coûtent plus cher qu'un aller-retour.
  const chargerEvenements = useCallback(() => {
    axios.get(`${API}/agenda`).then(res => setEvenements(res.data)).catch(() => {});
  }, []);

  // L'instance axios partagée pose déjà le jeton sur chaque requête
  // (src/api.js) : inutile de le remettre à la main ici.
  useEffect(() => {
    // Le squelette s'efface quand les trois sources qui alimentent les
    // indicateurs sont arrivées ; vendeurs, rappels et alertes suivent sans
    // retenir l'affichage.
    Promise.allSettled([
      axios.get(`${API}/prospects`).then(res => setProspects(res.data)),
      axios.get(`${API}/projets`).then(res => setProjets(res.data)),
      axios.get(`${API}/contrats-assurance`).then(res => setContrats(res.data)),
      axios.get(`${API}/dossiers`).then(res => setDossiers(res.data)),
    ]).finally(() => setChargement(false));
    axios.get(`${API}/rappels`).then(res => setRappels(res.data)).catch(() => {});
    chargerEvenements();
    axios.get(`${API}/alertes-banques`).then(res => setAlertesBanques(res.data)).catch(() => {});
  }, [chargerEvenements]);

  const marquerContacte = (id) => {
    setRappelsLoading(true);
    axios.post(`${API}/rappels/${id}/contacter`)
      .then(() => setRappels(prev => prev.filter(r => r.id !== id)))
      .finally(() => setRappelsLoading(false));
  };

  // ── Indicateurs globaux (non filtrés par période) ──────────────────────────
  const total = prospects.length;

  // Un dossier vivant — parti aux banques ou déjà validé — est une affaire en
  // cours : c'est lui qui fait foi, et non plus le projet dont il est issu.
  //
  // Deux raisons d'en tenir compte dès « En cours ». D'abord un dossier chez
  // les banques est ce qu'il y a de plus avancé dans le tunnel : le travail
  // est fait, il ne manque qu'une réponse. Ensuite, sans cela, un dossier issu
  // d'un projet à échéance lointaine — ou monté sans projet — ne comptait
  // nulle part, alors qu'un projet à peine esquissé comptait, lui.
  //
  // L'exclusion du projet repris est ce qui empêche de compter deux fois la
  // même affaire. Sans elle, rien ne le signalerait : les chiffres seraient
  // seulement trop hauts, et c'est le patron qui les lit.
  //
  // Contrepartie assumée : un dossier sans suite qu'on oublie de passer en
  // « Refusé » reste dans le compteur indéfiniment. Le statut refusé est ce
  // qui l'en sort.
  const dossiersActifs = dossiers.filter(d => d.statut !== 'Refusé');
  const dossiersValides = dossiers.filter(d => d.statut === 'Validé');
  const projetsRepris = new Set(dossiersActifs.map(d => d.projet_id).filter(Boolean));

  const projetParId = {};
  projets.forEach(p => { projetParId[p.id] = p; });

  /** Commission d'un dossier : celle négociée si elle est saisie, sinon celle
   *  que prévoyait le projet d'origine. Un dossier parti aux banques n'a pas
   *  encore de montant convenu — le laisser à zéro ferait baisser le chiffre
   *  d'affaires prévisionnel au moment précis où l'affaire avance. */
  const commissionDossier = (d) => {
    if (d.commission != null) return d.commission;
    const projet = d.projet_id ? projetParId[d.projet_id] : null;
    if (!projet) return 0;
    return (d.montant_total || 0) * ((projet.taux_commission || 0) / 100);
  };

  const projetsChauds = projets.filter(p => {
    if (projetsRepris.has(p.id)) return false;
    if (!p.date_echeance) return false;
    if (['Gagné facturé', 'Perdu', 'Accord envoyé'].includes(p.statut_projet)) return false;
    const echeance = new Date(p.date_echeance);
    echeance.setHours(23, 59, 59, 0); // fin de journée : évite les décalages UTC
    const jours = Math.ceil((echeance - new Date()) / 86400000);
    return jours >= 0 && jours <= REGLES.joursAffaireChaude;
  });
  // Toute affaire vivante compte une fois, et une seule : au stade projet, au
  // stade dossier aux banques, ou au stade dossier validé.
  const affairesChaudes = projetsChauds.length + dossiersActifs.length;

  const volumeFinance =
    projetsChauds.reduce((s, p) => s + (p.montant_financement || 0), 0)
    + dossiersActifs.reduce((s, d) => s + (d.montant_total || 0), 0);

  const ca =
    projetsChauds.reduce(
      (s, p) => s + (p.montant_financement || 0) * ((p.taux_commission || 0) / 100), 0)
    + dossiersActifs.reduce((s, d) => s + commissionDossier(d), 0);

  // Une commission oubliée ne se voit pas dans un total : elle en est
  // seulement absente. Le tableau de bord le dit donc explicitement.
  const commissionsManquantes = dossiersValides.filter(d => d.commission == null).length;
  const projetsGagnesFactures = projets.filter(p => p.statut_projet === 'Gagné facturé');
  const montantGagnesFactures = projetsGagnesFactures.reduce(
    (s, p) => s + (p.montant_a_facturer || 0), 0);

  const contratsActifAssurance = contrats.filter(c => c.statut === 'Actif').length;
  // Les affaires chaudes de l'assurance sont les contrats en cours de
  // souscription. Elles ne partagent aucune donnée avec celles du crédit
  // (table `projets`) : les deux compteurs ne peuvent pas se contaminer.
  const affairesChaudesAssurance = contrats.filter(c => c.statut === 'En attente').length;
  // Le total affiché sur la carte. Les deux comptes viennent de deux tables
  // distinctes et le restent : on les additionne pour l'affichage, rien de
  // plus.
  const affairesChaudesTotal = affairesChaudes + affairesChaudesAssurance;
  const primeGlobale = contrats.filter(c => c.statut === 'Actif')
    .reduce((s, c) => s + (c.prime_annuelle || 0), 0);
  const primeCabinet = primeGlobale * REGLES.partCabinetSurPrime;

  const contratsActifsGroupes = useMemo(() => {
    const groupes = {};
    contrats.filter(c => c.statut === 'Actif').forEach(c => {
      if (!c.prospect_id) return;
      groupes[c.prospect_id] = (groupes[c.prospect_id] || 0) + 1;
    });
    return Object.entries(groupes)
      .map(([pid, count]) => {
        const p = prospects.find(x => x.id === parseInt(pid));
        return { prospect_id: parseInt(pid), nom: p?.nom_societe || `#${pid}`, count };
      })
      .sort((a, b) => b.count - a.count || a.nom.localeCompare(b.nom, 'fr'));
  }, [contrats, prospects]);

  // Mêmes couleurs de statut que dans le reste du CRM : un statut doit garder
  // la même teinte d'un écran à l'autre, sinon la couleur n'apprend rien.
  const COULEURS_STATUT = {
    'En attente': '#d97706', 'Validé': '#0f9d6e', 'Sans suite': '#8b93a1',
    'Perdu': '#dc2626', 'Perdu mais à relancer': '#ea580c',
    "À l'étude": '#2563eb', 'Gagné': '#047857', 'Signé': '#7c3aed',
  };

  const statutsCounts = {};
  prospects.forEach(p => { statutsCounts[p.statut] = (statutsCounts[p.statut] || 0) + 1; });

  const fmtEuros = (val) => (!val && val !== 0)
    ? '0 €' : Math.round(val).toLocaleString('fr-FR') + ' €';

  const versProspectsChauds = () => {
    // Les entreprises des deux sources : un clic qui n'en montrerait qu'une
    // partie ferait douter du compteur lui-même.
    const ids = [...new Set([
      ...projetsChauds.map(p => p.prospect_id),
      ...dossiersActifs.map(d => d.prospect_id),
    ].filter(Boolean))];
    navigate(ids.length ? `/prospects?ids=${ids.join(',')}` : '/prospects');
  };
  // Les affaires elles-mêmes, une ligne chacune. La liste d'entreprises qu'ouvrait
  // le clic ne montrait aucune affaire : impossible d'en supprimer une sans passer
  // par la fiche, où le bouton rouge « Supprimer » vise l'entreprise.
  const nomEntreprise = (id) =>
    (prospects.find(p => p.id === id) || {}).nom_societe || `Entreprise n° ${id}`;
  const fmtJour = (d) => new Date(d).toLocaleDateString('fr-FR');

  const lignesChaudes = (metier) => metier === 'credit'
    ? [
        ...projetsChauds.map(p => ({
          cle: `p${p.id}`, nature: 'projet', id: p.id, prospect_id: p.prospect_id,
          titre: p.materiel || p.nom_produit || `Projet n° ${p.id}`,
          detail: p.date_echeance ? `Projet · échéance ${fmtJour(p.date_echeance)}` : 'Projet',
          montant: p.montant_financement })),
        ...dossiersActifs.map(d => ({
          cle: `d${d.id}`, nature: 'dossier', id: d.id, prospect_id: d.prospect_id,
          titre: `Dossier ${d.numero}`, detail: `Dossier de financement · ${d.statut}`,
          montant: d.montant_total, projet_id: d.projet_id })),
      ]
    : contrats.filter(c => c.statut === 'En attente').map(c => ({
        cle: `c${c.id}`, nature: 'contrat', id: c.id, prospect_id: c.prospect_id,
        titre: `${TYPE_CONTRAT_LABELS[c.type_contrat] || c.type_contrat || 'Contrat'}${c.compagnie ? ` · ${c.compagnie}` : ''}`,
        detail: c.date_echeance ? `Contrat en attente · échéance ${fmtJour(c.date_echeance)}` : 'Contrat en attente',
        montant: c.prime_annuelle, parAn: true }));

  const supprimerAffaire = async (ligne) => {
    const origine = ligne.nature === 'dossier' && ligne.projet_id
      ? projets.find(p => p.id === ligne.projet_id) : null;
    // Un dossier supprimé laisse son projet d'origine, qui peut redevenir une
    // affaire chaude : on le dit avant, pas après.
    const avertissement = origine
      ? `\n\nLe projet d'origine (« ${origine.materiel || origine.nom_produit || `n° ${origine.id}`} ») n'est pas supprimé : s'il est encore dans les ${REGLES.joursAffaireChaude} jours, il redeviendra une affaire chaude.`
      : '';
    if (!window.confirm(`Supprimer cette affaire chaude ?\n\n${ligne.titre} — ${nomEntreprise(ligne.prospect_id)}\n\nL'entreprise, elle, n'est pas supprimée.${avertissement}`)) return;
    const route = { projet: 'projets', dossier: 'dossiers', contrat: 'contrats-assurance' }[ligne.nature];
    try {
      await axios.delete(`${API}/${route}/${ligne.id}`);
      if (ligne.nature === 'projet') setProjets(prev => prev.filter(p => p.id !== ligne.id));
      if (ligne.nature === 'dossier') setDossiers(prev => prev.filter(d => d.id !== ligne.id));
      if (ligne.nature === 'contrat') setContrats(prev => prev.filter(c => c.id !== ligne.id));
      setErreurAffaire('');
    } catch (err) {
      // Le serveur motive son refus (rôle, élément introuvable) : on l'affiche.
      setErreurAffaire(err.response?.data?.detail || 'La suppression a échoué.');
    }
  };

  const versContratsParStatut = (statut) => {
    const ids = [...new Set(contrats.filter(x => x.statut === statut)
      .map(x => x.prospect_id).filter(Boolean))];
    // Sans entreprise à montrer, on ne navigue pas : `/prospects` sans filtre
    // affichait *toutes* les entreprises, ce qui se lit comme « voici vos
    // affaires chaudes » alors qu'il n'y en a aucune.
    if (!ids.length) return;
    navigate(`/prospects?ids=${ids.join(',')}`);
  };

  // Rendu d'attente : mêmes groupes, mêmes emplacements, sans les valeurs.
  // Écrit à part plutôt qu'en conditions dispersées dans la page — le rendu
  // réel reste ainsi lisible d'un bloc.
  if (chargement) {
    return (
      <div>
        <div style={{ marginBottom: E.xl }}>
          <h1 style={T.titre}>Tableau de bord</h1>
          <p style={{ ...T.sousTitre, marginTop: '2px' }}>
            Vue d'ensemble de votre activité commerciale
          </p>
        </div>
        {[['Portefeuille', 4], ['Crédit', 6], ['Assurance', 4]].map(([titre, nb]) => (
          <div key={titre} style={{ marginBottom: E.xl }}>
            <TitreGroupe>{titre}</TitreGroupe>
            <SqueletteIndicateurs nombre={nb} colonnes={grilleIndicateurs} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* ── En-tête ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: E.xl }}>
        <h1 style={T.titre}>Tableau de bord</h1>
        <p style={{ ...T.sousTitre, marginTop: '2px' }}>
          Vue d'ensemble de votre activité commerciale
        </p>
      </div>

      {/* ── Alertes banques ─────────────────────────────────────────────── */}
      {alertesBanques.length > 0 && (
        <div style={{
          background: C.alerteFond, border: `1px solid #f5d9a8`,
          borderRadius: RAYON.lg, padding: `${E.md} ${E.lg}`, marginBottom: E.xl,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: E.sm, marginBottom: E.md }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#8a5300' }}>
              Banques sans réponse depuis plus de 3 jours
            </span>
            <span style={pastille('alerte')}>{alertesBanques.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: E.sm }}>
            {alertesBanques.map(a => (
              <div key={a.suivi_id}
                onClick={() => navigate(`/dossier?dossier_id=${a.dossier_id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: E.md, background: C.surface,
                  borderRadius: RAYON.sm, padding: `${E.sm} ${E.md}`,
                  border: `1px solid ${C.bordure}`, cursor: 'pointer',
                }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.texte }}>
                    {a.dossier_numero} — {a.dossier_raison_sociale}
                  </div>
                  <div style={{ fontSize: '12px', color: C.texteFaible, marginTop: '1px' }}>
                    {a.banque_nom}{a.banque_email ? ` · ${a.banque_email}` : ''}
                  </div>
                </div>
                <span style={pastille('alerte')}>{a.jours_attente} j</span>
                <span style={{ color: C.texteFaible }}>→</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Portefeuille / Assurance ───────────────────────────────────── */}
      {/* La rangée mêle le portefeuille et l'assurance à la demande du
          cabinet : ce sont les chiffres qu'on veut voir en premier le matin,
          d'où qu'ils viennent. Gagnés / Signés, Perdus et Taux de conversion
          leur ont cédé la place et ont été supprimés. */}
      <div style={{ marginBottom: E.xl }}>
        <TitreGroupe precision={`${total} entreprise(s) · ${contrats.length} contrat(s)`}>
          Portefeuille / Assurance
        </TitreGroupe>
        <div style={grilleIndicateurs}>
          <Indicateur libelle="Entreprises" valeur={total} precision="dans la base"
                      accent="bleu" onClick={() => navigate('/prospects')} />
          {/* Deux cartes portent « Contrats actifs » dans cet écran et ne
              comptent pas la même chose : le métier est donc écrit dans le
              libellé, pas seulement dans la précision. */}
          <Indicateur libelle="Contrats actifs (assurance)" valeur={contratsActifAssurance}
                      precision="statut Actif" accent="vert"
                      onClick={() => setContratsActifsModal(true)} />
          <Indicateur libelle="Prime globale" valeur={fmtEuros(primeGlobale)}
                      accent="violet" precision="cumul des primes annuelles" />
          <Indicateur libelle="Prime cabinet" valeur={fmtEuros(primeCabinet)}
                      accent="orange"
                      precision={`${Math.round(REGLES.partCabinetSurPrime * 100)} % de la prime globale`} />
        </div>
      </div>

      {/* ── Crédit ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: E.xl }}>
        <TitreGroupe precision={`${projets.length} projet(s)`}>Crédit</TitreGroupe>
        <div style={grilleIndicateurs}>
          {/* Une seule carte, et le total des deux métiers : c'est le chiffre
              qu'on veut voir d'un coup d'œil le matin. Le détail se demande au
              clic, qui propose crédit ou assurance — un sélecteur posé sur la
              carte obligeait à choisir avant même d'avoir lu le total. Les
              deux tables restent séparées : rien ne se mélange, on additionne
              seulement deux comptes pour l'affichage. */}
          <Indicateur libelle="Affaires chaudes" valeur={affairesChaudesTotal}
                      precision="crédit et assurance"
                      accent="rouge" onClick={() => setChoixChaudes(true)} compact />
          {/* Volume financé et montant à facturer se lisaient autrefois dans
              deux cartes éloignées l'une de l'autre, aux deux bouts de la
              rangée. Réunis, ils disent d'un coup d'œil ce qui est engagé et
              ce qu'il reste à encaisser dessus. La carte occupe deux colonnes. */}
          <IndicateurDouble
            gauche={{ libelle: 'Volume financé', valeur: fmtEuros(volumeFinance),
                      precision: 'affaires chaudes + dossiers', accent: 'bleu' }}
            droite={{ libelle: 'Montant à facturer', valeur: fmtEuros(montantGagnesFactures),
                      precision: 'cumul à facturer', accent: 'vert',
                      onClick: () => navigate('/kanban') }} />
          <Indicateur libelle="CA prévisionnel" valeur={fmtEuros(ca)}
                      accent="orange"
                      precision={commissionsManquantes > 0
                        ? `${commissionsManquantes} commission(s) non renseignée(s)`
                        : 'commissions prévues et négociées'}
                      onClick={() => navigate('/dossiers')} compact />
          {/* Le Kanban nomme ce statut « Gagné facturé » ; la précision garde le
              lien entre les deux écrans, l'intitulé restant celui du métier. */}
          <Indicateur libelle="Contrats actifs (crédit)" valeur={projetsGagnesFactures.length}
                      precision="affaires gagnées et facturées"
                      accent="cyan" onClick={() => navigate('/kanban')} compact />
        </div>
      </div>

      {/* ── Agenda ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: E.lg }}>
        <AgendaTableauBord evenements={evenements}
                           user={user}
                           onChangement={chargerEvenements}
                           onVoirAgenda={() => navigate('/agenda')} />
      </div>

      {/* ── Rappels ─────────────────────────────────────────────────────── */}
      {rappels.length > 0 && (
        <div style={{ marginBottom: E.lg }}>
          <Carte titre="Prospects à relancer"
                 precision="sans interaction depuis plus de 30 jours"
                 actions={<span style={pastille('alerte')}>{rappels.length}</span>}
                 sansPadding>
            <div>
              {rappels.slice(0, 8).map((r, i) => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: E.md, padding: `${E.md} ${E.lg}`,
                  borderBottom: i < Math.min(8, rappels.length) - 1
                    ? `1px solid ${C.bordure}` : 'none',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: '13.5px', fontWeight: 600, color: C.texte }}>
                      {r.nom_societe}
                    </span>
                    <span style={{ marginLeft: E.md, fontSize: '12px',
                                   color: r.jours_sans_contact >= 60 ? C.danger : C.alerte,
                                   fontWeight: 600 }}>
                      {r.jours_sans_contact} j sans contact
                    </span>
                    {r.dernier_contact && (
                      <span style={{ marginLeft: E.sm, fontSize: '12px', color: C.texteFaible }}>
                        dernier : {new Date(r.dernier_contact).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                  <button onClick={() => marquerContacte(r.id)} disabled={rappelsLoading}
                          style={{ ...bouton('secondaire'), flexShrink: 0 }}>
                    Marquer contacté
                  </button>
                </div>
              ))}
            </div>
          </Carte>
        </div>
      )}

      {/* ── Répartition par statut ──────────────────────────────────────── */}
      <Carte titre="Répartition par statut" precision={`${total} entreprise(s)`}>
        {Object.entries(statutsCounts).length === 0 ? (
          <p style={{ color: C.texteFaible, fontSize: '13px', margin: 0 }}>Aucune donnée.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: E.md }}>
            {Object.entries(statutsCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([statut, count]) => (
                <div key={statut}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                marginBottom: E.xs }}>
                    <span style={{ fontSize: '13px', color: C.texte, display: 'flex',
                                   alignItems: 'center', gap: E.sm }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                     background: COULEURS_STATUT[statut] || C.texteFaible }} />
                      {statut}
                    </span>
                    <span style={{ fontSize: '12.5px', color: C.texteFaible }}>
                      {count} / {total}
                    </span>
                  </div>
                  <div style={{ background: C.fond, borderRadius: RAYON.rond, height: '7px' }}>
                    <div style={{
                      background: COULEURS_STATUT[statut] || C.texteFaible,
                      height: '7px', borderRadius: RAYON.rond,
                      width: total ? `${(count / total) * 100}%` : '0%',
                      transition: 'width 400ms ease',
                    }} />
                  </div>
                </div>
              ))}
          </div>
        )}
      </Carte>

      {/* ── Fenêtre : contrats actifs ───────────────────────────────────── */}
      {/* Choix du métier, demandé au clic sur les affaires chaudes. Chaque
          branche annonce son compte : une branche vide se voit avant le clic,
          au lieu d'ouvrir un écran qui ne montre rien — ou pire, tout. */}
      {choixChaudes && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 1000, padding: E.lg }}
             onClick={() => setChoixChaudes(false)}>
          <div style={{ background: C.surface, borderRadius: RAYON.lg, width: 420,
                        maxWidth: '100%', boxShadow: '0 20px 60px rgba(16,24,40,0.24)' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ padding: E.lg, borderBottom: `1px solid ${C.bordure}` }}>
              <h2 style={{ fontSize: '15px', fontWeight: 600, color: C.texte, margin: 0 }}>
                Affaires chaudes
              </h2>
              <div style={{ fontSize: '12.5px', color: C.texteFaible, marginTop: '2px' }}>
                {affairesChaudesTotal} au total — quel métier voulez-vous voir ?
              </div>
            </div>
            <div style={{ padding: E.lg, display: 'flex', flexDirection: 'column', gap: E.sm }}>
              {[
                { cle: 'credit', titre: 'Crédit', nombre: affairesChaudes,
                  detail: `projets sous ${REGLES.joursAffaireChaude} jours et dossiers en cours`,
                  aller: () => { setChoixChaudes(false); setErreurAffaire(''); setListeChaudes('credit'); } },
                { cle: 'assurance', titre: 'Assurance', nombre: affairesChaudesAssurance,
                  detail: 'contrats en cours de souscription',
                  aller: () => { setChoixChaudes(false); setErreurAffaire(''); setListeChaudes('assurance'); } },
                // Une affaire véhicule vit dans les deux autres (un projet et un
                // contrat) : elle n'a donc pas de compte à elle. Cette ligne
                // sert à en créer une.
                { cle: 'vehicule', titre: 'Véhicule', nombre: 0,
                  detail: 'financement et assurance — en créer une', aller: null },
              ].map(m => ({
                ...m,
                // Une branche vide reste cliquable, et mène à la création :
                // c'est précisément quand il n'y a rien qu'on veut en créer.
                detail: m.nombre > 0 || m.cle === 'vehicule' ? m.detail : 'aucune — en créer une',
                aller: m.nombre > 0
                  ? m.aller
                  : () => { setChoixChaudes(false); navigate(`/prospects?nouvelle_affaire=${m.cle}`); },
              })).map(m => (
                <button key={m.cle} onClick={m.aller}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: E.md, width: '100%', textAlign: 'left',
                          padding: `${E.md} ${E.lg}`, borderRadius: RAYON.sm,
                          border: `1px solid ${C.bordure}`, background: C.surface,
                          fontFamily: T.police, cursor: 'pointer',
                        }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '14px', fontWeight: 600,
                                   color: C.texte }}>{m.titre}</span>
                    <span style={{ display: 'block', fontSize: '12px', color: C.texteFaible,
                                   marginTop: '1px' }}>
                      {m.detail}
                    </span>
                  </span>
                  <span style={{ ...T.chiffre, fontSize: '20px', flexShrink: 0,
                                 color: m.nombre > 0 ? C.texte : C.texteFaible }}>
                    {m.nombre > 0 ? m.nombre : '+'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Les affaires chaudes d'un métier, une ligne chacune. La corbeille n'est
          montrée qu'aux admins — et c'est le serveur qui refuse aux autres. Elle
          supprime l'affaire, jamais l'entreprise. */}
      {listeChaudes && (() => {
        const lignes = lignesChaudes(listeChaudes);
        const estAdmin = user?.role === 'admin';
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: E.lg }}
               onClick={() => setListeChaudes(null)}>
            <div style={{ background: C.surface, borderRadius: RAYON.lg, width: 620,
                          maxWidth: '100%', maxHeight: '82vh', display: 'flex', flexDirection: 'column',
                          boxShadow: '0 20px 60px rgba(16,24,40,0.24)' }}
                 onClick={e => e.stopPropagation()}>
              <div style={{ padding: E.lg, borderBottom: `1px solid ${C.bordure}`,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: E.md }}>
                <div>
                  <h2 style={{ fontSize: '15px', fontWeight: 600, color: C.texte, margin: 0 }}>
                    Affaires chaudes — {listeChaudes === 'credit' ? 'Crédit' : 'Assurance'}
                  </h2>
                  <div style={{ fontSize: '12.5px', color: C.texteFaible, marginTop: '2px' }}>
                    {lignes.length} affaire(s)
                    {estAdmin ? ' · la corbeille supprime l\'affaire, pas l\'entreprise' : ' · seul un administrateur peut en supprimer'}
                  </div>
                </div>
                <button onClick={() => setListeChaudes(null)}
                        style={{ ...bouton('discret'), fontSize: '18px', lineHeight: 1 }}>×</button>
              </div>

              {erreurAffaire && (
                <div style={{ padding: `${E.sm} ${E.lg}`, fontSize: '12.5px', color: C.danger,
                              background: C.dangerFond }}>{erreurAffaire}</div>
              )}

              <div style={{ overflowY: 'auto' }}>
                {lignes.length === 0 ? (
                  <div style={{ padding: E.xl, textAlign: 'center', fontSize: '13px', color: C.texteFaible }}>
                    Plus aucune affaire chaude dans ce métier.
                  </div>
                ) : lignes.map((l, i) => (
                  <div key={l.cle} style={{
                    display: 'flex', alignItems: 'center', gap: E.md, padding: `${E.md} ${E.lg}`,
                    borderBottom: i < lignes.length - 1 ? `1px solid ${C.bordure}` : 'none' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.texte,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.titre}</div>
                      <div style={{ fontSize: '12px', color: C.texteFaible, marginTop: '1px' }}>{l.detail}</div>
                      <button onClick={() => navigate(`/prospects/${l.prospect_id}`)}
                              style={{ ...bouton('discret'), padding: 0, fontSize: '12px', fontWeight: 600,
                                       color: C.marine }}>
                        {nomEntreprise(l.prospect_id)} →
                      </button>
                    </div>
                    <div style={{ ...T.chiffre, fontSize: '15px', flexShrink: 0, textAlign: 'right' }}>
                      {l.montant ? `${fmtEuros(l.montant)}${l.parAn ? ' /an' : ''}` : '—'}
                    </div>
                    {estAdmin && (
                      <button onClick={() => supprimerAffaire(l)} title="Supprimer cette affaire (l'entreprise reste)"
                              style={{ ...bouton('danger'), flexShrink: 0, padding: '6px 10px' }}>
                        Supprimer
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ padding: `${E.md} ${E.lg}`, borderTop: `1px solid ${C.bordure}`,
                            display: 'flex', justifyContent: 'space-between', gap: E.sm }}>
                <button onClick={() => { setListeChaudes(null); setChoixChaudes(true); }}
                        style={bouton('discret')}>← Retour</button>
                {lignes.length > 0 && (
                  <button onClick={() => { const m = listeChaudes; setListeChaudes(null);
                                           if (m === 'credit') versProspectsChauds(); else versContratsParStatut('En attente'); }}
                          style={bouton('secondaire')}>Voir les entreprises</button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {contratsActifsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 1000, padding: E.lg }}
             onClick={() => setContratsActifsModal(false)}>
          <div style={{ background: C.surface, borderRadius: RAYON.lg, width: 440,
                        maxWidth: '100%', maxHeight: '80vh', display: 'flex',
                        flexDirection: 'column', boxShadow: '0 20px 60px rgba(16,24,40,0.24)' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'flex-start', padding: E.lg,
                          borderBottom: `1px solid ${C.bordure}` }}>
              <div>
                <h2 style={{ fontSize: '15px', fontWeight: 600, color: C.texte, margin: 0 }}>
                  Contrats actifs
                </h2>
                <p style={{ ...T.sousTitre, marginTop: '2px' }}>
                  {contratsActifAssurance} contrat{contratsActifAssurance > 1 ? 's' : ''} ·{' '}
                  {contratsActifsGroupes.length} entreprise{contratsActifsGroupes.length > 1 ? 's' : ''}
                </p>
              </div>
              <button onClick={() => setContratsActifsModal(false)}
                      style={{ background: 'none', border: 'none', fontSize: '20px',
                               cursor: 'pointer', color: C.texteFaible, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: E.sm }}>
              {contratsActifsGroupes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: E.xxl, color: C.texteFaible,
                              fontSize: '13px' }}>Aucun contrat actif</div>
              ) : contratsActifsGroupes.map(({ prospect_id, nom, count }) => (
                <div key={prospect_id}
                     onClick={() => { navigate(`/prospects/${prospect_id}`); setContratsActifsModal(false); }}
                     style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center', gap: E.md, padding: `${E.md} ${E.md}`,
                              borderRadius: RAYON.sm, cursor: 'pointer' }}
                     onMouseEnter={e => e.currentTarget.style.background = C.fond}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize: '13.5px', color: C.texte }}>{nom}</span>
                  <span style={pastille('succes')}>
                    {count} contrat{count > 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
